import { useState, useEffect, useCallback, useRef } from 'react'

export interface FavoriteRoute {
  id: string
  lineId: string
  routeCode: string
  routeDescription: string
  stopCode: string
  stopName: string
}

const STORAGE_KEY = 'nextbus-favorites'

function loadFromLocalStorage(): FavoriteRoute[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveToLocalStorage(favorites: FavoriteRoute[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites))
}

// Favorites are stored locally in the browser (no sign-in required)
export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteRoute[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const initialLoadDone = useRef(false)

  // Load favorites on mount
  useEffect(() => {
    setFavorites(loadFromLocalStorage())
    setIsLoading(false)
    initialLoadDone.current = true
  }, [])

  // Save favorites when they change
  useEffect(() => {
    if (isLoading || !initialLoadDone.current) return
    saveToLocalStorage(favorites)
  }, [favorites, isLoading])

  const addFavorite = useCallback((favorite: Omit<FavoriteRoute, 'id'>) => {
    const id = `${favorite.lineId}-${favorite.routeCode}-${favorite.stopCode}`
    setFavorites(prev => {
      if (prev.some(f => f.id === id)) return prev
      return [...prev, { ...favorite, id }]
    })
  }, [])

  const removeFavorite = useCallback((lineId: string, routeCode: string, stopCode: string) => {
    const id = `${lineId}-${routeCode}-${stopCode}`
    setFavorites(prev => prev.filter(f => f.id !== id))
  }, [])

  const toggleFavorite = useCallback((favorite: Omit<FavoriteRoute, 'id'>) => {
    const id = `${favorite.lineId}-${favorite.routeCode}-${favorite.stopCode}`
    setFavorites(prev => {
      if (prev.some(f => f.id === id)) {
        return prev.filter(f => f.id !== id)
      }
      return [...prev, { ...favorite, id }]
    })
  }, [])

  const isFavorite = useCallback((lineId: string, routeCode: string, stopCode: string): boolean => {
    const id = `${lineId}-${routeCode}-${stopCode}`
    return favorites.some(f => f.id === id)
  }, [favorites])

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    isLoading
  }
}
